import {useState} from 'react';
import {
  BlockStack,
  InlineStack,
  Card,
  Text,
  SkeletonBodyText,
} from '@shopify/polaris';
import {useI18n} from '@shopify/react-i18n';
import {Toggle} from 'components/Toggle';
import {useQuery, useMutation} from '@shopify/react-graphql';
import {ShopLocale} from 'utilities/use-shop-locales-and-markets';
import {useToast} from 'utilities/toasts';
import {useShop} from '@web-utilities/shop';

import MachineTranslationSettingsQuery from './graphql/MachineTranslationSettingsQuery.graphql';
import EnableAutoSyncMutation from './graphql/EnableAutoSyncMutation.graphql';
import DisableAutoSyncMutation from './graphql/DisableAutoSyncMutation.graphql';

interface Props {
  currentLocale: ShopLocale;
}

export function TranslationsSyncCard({currentLocale}: Props) {
  const [i18n] = useI18n();
  const toast = useToast();
  const {
    features: {autoSyncSupport},
  } = useShop();
  const [toggling, setToggling] = useState(false);
  const {data, loading: machineTranslationsSettingsLoading} = useQuery(
    MachineTranslationSettingsQuery,
  );
  const settingsForCurrentLocale = data?.machineTranslationSettings?.find(
    ({locale}) => locale === currentLocale.locale,
  );
  const enableAutoSyncMutation = useMutation(EnableAutoSyncMutation);
  const disableAutoSyncMutation = useMutation(DisableAutoSyncMutation);

  const enable = async () => {
    setToggling(true);
    const {data} = await enableAutoSyncMutation({
      variables: {
        locale: currentLocale.locale,
      },
      refetchQueries: ['MachineTranslationSettings'],
    });

    if (data?.autoSyncEnable.userErrors.length) {
      // Show common server error message because user can't do anything if
      // the mutation fails.
      toast.show({
        content: i18n.translate('Common.serverError'),
        error: true,
      });
    } else {
      toast.show({
        content: i18n.translate('TranslationsSyncCard.autoSyncEnabled'),
        error: false,
      });
    }
    setToggling(false);
  };

  const disable = async () => {
    setToggling(true);
    const {data} = await disableAutoSyncMutation({
      variables: {
        locale: currentLocale.locale,
      },
      refetchQueries: ['MachineTranslationSettings'],
    });

    if (data?.autoSyncDisable.userErrors.length) {
      // Show common server error message because user can't do anything if
      // the mutation fails.
      toast.show({
        content: i18n.translate('Common.serverError'),
        error: true,
      });
    } else {
      toast.show({
        content: i18n.translate('TranslationsSyncCard.autoSyncDisabled'),
        error: false,
      });
    }
    setToggling(false);
  };

  if (machineTranslationsSettingsLoading) {
    return (
      <Card>
        <SkeletonBodyText lines={2} />
      </Card>
    );
  }

  if (
    !autoSyncSupport ||
    (!machineTranslationsSettingsLoading &&
      settingsForCurrentLocale === undefined)
  ) {
    return null;
  }

  return (
    <Card padding="400" roundedAbove="sm">
      <BlockStack gap="200">
        <InlineStack wrap={false} align="space-between">
          <Text as="h2" variant="headingSm">
            {i18n.translate('TranslationsSyncCard.title')}
          </Text>
          <Toggle
            ariaLabel={i18n.translate('TranslationsSyncCard.title')}
            on={settingsForCurrentLocale?.autoSyncEnabled ?? false}
            disabled={machineTranslationsSettingsLoading}
            onChange={() => {
              // Don't allow onChange to trigger while a mutation is in progress
              if (toggling) return;

              if (settingsForCurrentLocale?.autoSyncEnabled) {
                disable();
              } else {
                enable();
              }
            }}
          />
        </InlineStack>
        <Text as="p" variant="bodySm">
          {i18n.translate('TranslationsSyncCard.description')}
        </Text>
      </BlockStack>
    </Card>
  );
}
